import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate } from '../../lib/format'
import type { KycDoc, ListResponse } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton, StatusBadge,
} from '../../components/ui'
import { DocIcon, IdIcon } from '../../components/icons'

type LeaderKycDoc = KycDoc & { miner_uid?: string; miner_name?: string }

const FILTERS = [
  { value: 'submitted', label: 'Awaiting review' },
  { value: '', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

export default function KycReview() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('submitted')
  const [openId, setOpenId] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'kyc', status],
    queryFn: () => api.get<ListResponse<LeaderKycDoc>>(
      `/leader/kyc${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  })

  const review = useMutation({
    mutationFn: ({ uid, docId, action }: { uid: string; docId: string; action: 'approve' | 'reject' }) =>
      api.post(`/leader/kyc/${uid}/${docId}/review`, { action, note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader'] })
      setOpenId(null)
      setNote('')
    },
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Identity verification</h1>
          <p className="page-head__sub">
            Check that each document matches the member who submitted it.
          </p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        {FILTERS.map((f) => (
          <Button key={f.label} size="sm"
                  variant={status === f.value ? 'primary' : 'secondary'}
                  onClick={() => setStatus(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>

      {review.isError && <Alert tone="danger">{(review.error as Error).message}</Alert>}

      <Card>
        <CardHeader title="Documents"
                    sub={items.length ? `${items.length} document${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="72%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<IdIcon size={22} />}
                        title={status === 'submitted' ? 'Nothing awaiting review' : 'No documents'}
                        body={status === 'submitted'
                          ? 'Every submitted document has been reviewed.'
                          : 'Try a different filter.'} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Member</th><th>Type</th><th>Uploaded</th><th>Status</th><th /><th /></tr>
                </thead>
                <tbody>
                  {items.map((d, i) => {
                    const key = `${d.miner_uid}-${d.id ?? i}`
                    const href = d.url || d.file_url
                    const pending = d.status === 'submitted'
                    return (
                      <Fragment key={key}>
                        <tr>
                          <td>{d.miner_name || d.miner_uid || '—'}</td>
                          <td>{d.doc_type || '—'}</td>
                          <td>{formatDate(d.uploaded_at)}</td>
                          <td><StatusBadge status={d.status} /></td>
                          <td>
                            {href && (
                              <a href={href} target="_blank" rel="noreferrer"
                                 style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
                                <DocIcon size={15} /> View
                              </a>
                            )}
                          </td>
                          <td className="table__num">
                            {pending && d.id && d.miner_uid && (
                              <Button size="sm" variant="secondary"
                                      onClick={() => { setOpenId(openId === key ? null : key); setNote('') }}>
                                {openId === key ? 'Close' : 'Review'}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {openId === key && d.id && d.miner_uid && (
                          <tr>
                            <td colSpan={6} style={{ background: 'var(--bg-sunken)' }}>
                              <div className="stack" style={{ padding: 'var(--sp-2) 0' }}>
                                <Alert tone="info">
                                  Open the document and confirm the name and photo match
                                  <strong> {d.miner_name || 'this member'}</strong> before approving.
                                </Alert>
                                <Input label="Review note (optional)"
                                       placeholder="Sent to the member with your decision"
                                       value={note}
                                       onChange={(e) => setNote(e.target.value)} />
                                <div className="row">
                                  <Button size="sm" loading={review.isPending}
                                          onClick={() => review.mutate({
                                            uid: d.miner_uid!, docId: d.id!, action: 'approve',
                                          })}>
                                    Approve
                                  </Button>
                                  <Button size="sm" variant="danger" loading={review.isPending}
                                          onClick={() => review.mutate({
                                            uid: d.miner_uid!, docId: d.id!, action: 'reject',
                                          })}>
                                    Reject
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
